<script setup lang="ts">
import { endpoints } from '~/config/endpoints.config'

const { t } = useI18n()
const localePath = useLocalePath()

const enrichmentEndpoints: ApiTocCategoryEndpoint[] = endpoints.filter(
  endpoint => [
    'companies-enrich-from-domain',
    'companies-enrich-from-email',
    'companies-enrich-from-social-network-url',
  ].includes(endpoint.id),
).map(
  endpoint => ({
    callsPerMonth: endpoint.callsPerMonth,
    color: 'blue',
    costPerCall: endpoint.costPerCall,
    costPerCompany: endpoint.costPerCompany,
    isNew: endpoint.isNew,
    method: endpoint.method,
    path: endpoint.path,
    text: endpoint.text,
    to: localePath({ path: endpoint.loc }),
    versions: endpoint.versions,
  }),
)

const aiEndpoints: ApiTocCategoryEndpoint[] = endpoints.filter(
  endpoint => [
    'companies-ask',
    'companies-fetch-context',
    'companies-search-prompt',
  ].includes(endpoint.id),
).map(
  endpoint => ({
    callsPerMonth: endpoint.callsPerMonth,
    color: 'green',
    costPerCall: endpoint.costPerCall,
    costPerCompany: endpoint.costPerCompany,
    isNew: endpoint.isNew,
    method: endpoint.method,
    path: endpoint.path,
    text: endpoint.text,
    to: localePath({ path: endpoint.loc }),
    versions: endpoint.versions,
  }),
)

const searchEndpoints: ApiTocCategoryEndpoint[] = endpoints.filter(
  endpoint => [
    'companies-search',
    'companies-search-name',
    'companies-search-prompt',
    'companies-search-similar',
  ].includes(endpoint.id),
).map(
  endpoint => ({
    callsPerMonth: endpoint.callsPerMonth,
    color: 'yellow',
    costPerCall: endpoint.costPerCall,
    costPerCompany: endpoint.costPerCompany,
    isNew: endpoint.isNew,
    method: endpoint.method,
    path: endpoint.path,
    text: endpoint.text,
    to: localePath({ path: endpoint.loc }),
    versions: endpoint.versions,
  }),
)
</script>

<template>
  <div class="flex w-full flex-col gap-52 py-24">
    <HomeHero
      :description="t('enrichment.description')"
      even
      orientation="horizontal"
      :title="t('enrichment.title')"
    >
      <template #headline>
        <UButton
          class="rounded-full"
          color="blue"
          :icon="getIcon('signals')"
          :label="t('enrichment.badge')"
          size="xs"
          variant="subtle"
        />
      </template>

      <template #default>
        <div class="relative flex w-full flex-col gap-4">
          <ApiTocCategoryEndpoint
            v-for="endpoint of enrichmentEndpoints"
            :key="endpoint.id"
            :calls-per-month="endpoint.callsPerMonth"
            :color="endpoint.color"
            :cost-per-call="endpoint.costPerCall"
            :cost-per-company="endpoint.costPerCompany"
            :is-new="endpoint.isNew"
            :method="endpoint.method"
            :path="endpoint.path"
            :text="t(endpoint.text!)"
            :to="endpoint.to"
          />
        </div>
      </template>

      <template #secondaryDescription>
        <BaseTestimonial
          avatar="/images/bases/BaseTestimonial/baptiste.jpeg"
          class="my-8"
          company="crisp.chat"
          name="Baptiste Jamin"
          :quote="t('testimonials.baptiste')"
          role="CEO"
        />
      </template>

      <template #links>
        <SectionGetStartedButtons />
      </template>
    </HomeHero>

    <HomeHero
      :description="t('search.description')"
      even
      orientation="horizontal"
      reverse
      :title="t('search.title')"
    >
      <template #headline>
        <UButton
          class="rounded-full"
          color="purple"
          :icon="getIcon('search')"
          :label="t('search.badge')"
          size="xs"
          variant="subtle"
        />
      </template>

      <template #default>
        <div class="relative flex w-full flex-col gap-4">
          <ApiTocCategoryEndpoint
            v-for="endpoint of searchEndpoints"
            :key="endpoint.id"
            :calls-per-month="endpoint.callsPerMonth"
            :color="endpoint.color"
            :cost-per-call="endpoint.costPerCall"
            :cost-per-company="endpoint.costPerCompany"
            :is-new="endpoint.isNew"
            :method="endpoint.method"
            :path="endpoint.path"
            :text="t(endpoint.text!)"
            :to="endpoint.to"
            :versions="endpoint.versions"
          />
        </div>
      </template>

      <template #links>
        <SectionGetStartedButtons />
      </template>
    </HomeHero>

    <HomeHero
      :description="t('ai.description')"
      even
      orientation="horizontal"
      :title="t('ai.title')"
    >
      <template #headline>
        <UButton
          class="rounded-full"
          color="green"
          :icon="getIcon('ai')"
          :label="t('ai.badge')"
          size="xs"
          variant="subtle"
        />
      </template>

      <template #default>
        <div class="relative flex w-full flex-col gap-4">
          <ApiTocCategoryEndpoint
            v-for="endpoint of aiEndpoints"
            :key="endpoint.id"
            :calls-per-month="endpoint.callsPerMonth"
            :color="endpoint.color"
            :cost-per-call="endpoint.costPerCall"
            :cost-per-company="endpoint.costPerCompany"
            :is-new="endpoint.isNew"
            :method="endpoint.method"
            :path="endpoint.path"
            :text="t(endpoint.text!)"
            :to="endpoint.to"
            :versions="endpoint.versions"
          />
        </div>
      </template>

      <template #links>
        <SectionGetStartedButtons />
      </template>
    </HomeHero>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "ai": {
      "badge": "AI features",
      "description": "Ask any question about a company or a list of companies. We'll generate the answer you need, so you get exactly the data you're looking for.",
      "title": "Ask any question about a company or fetch their full textual summary"
    },
    "enrichment": {
      "badge": "Enrichment features",
      "description": "Retrieve all the essential information on any company (100+ data points). Improve your user experience, enrich your CRM and marketing tools, or score your new leads. Missing a specific data point? We'll add it for you in just a few days.",
      "title": "Enrich companies by domain, email or social network URL"
    },
    "search": {
      "badge": "Search features",
      "description": "Query our database of companies by industry, location, number of employees, and 50+ other criteria. Build your own search engine for your product or save companies that match your ideal customer profile.",
      "title": "Search companies with conditions, by name or using a prompt"
    }
  },
  "es": {
    "ai": {
      "badge": "Características de IA",
      "description": "Haz cualquier pregunta sobre una empresa o una lista de empresas. Generaremos la respuesta que necesitas para que obtengas exactamente los datos que buscas.",
      "title": "Haga preguntas sobre una empresa o obtenga un resumen textual de su perfil"
    },
    "enrichment": {
      "badge": "Características de enriquecimiento",
      "description": "Recupera toda la información esencial de cualquier empresa (más de 100 datos). Mejora la experiencia de usuario, enriquece tus herramientas de CRM y marketing, o puntúa a tus nuevos leads. ¿Falta algún dato específico? Lo añadiremos en pocos días.",
      "title": "Enriquece empresas por dominio, email o URL de red social"
    },
    "search": {
      "badge": "Características de búsqueda",
      "description": "Consulta nuestra base de datos de empresas por industria, ubicación, número de empleados, y 50+ otros criterios. Construye tu propio buscador para tu producto o guarda empresas que coincidan con tu perfil de cliente ideal.",
      "title": "Busque empresas con condiciones, por nombre o usando un prompt"
    }
  },
  "fr": {
    "ai": {
      "badge": "Fonctionnalités IA",
      "description": "Posez n'importe quelle question sur une entreprise ou une liste d'entreprises. Nous générerons la réponse dont vous avez besoin, afin que vous obteniez exactement les données que vous recherchez.",
      "title": "Posez des questions sur une entreprise ou récupérez leur résumé complet de profil"
    },
    "enrichment": {
      "badge": "Fonctionnalités d'enrichissement",
      "description": "Récupérez toutes les informations essentielles sur n'importe quelle entreprise (plus de 100 points de données). Améliorez votre expérience utilisateur, enrichissez vos outils CRM et marketing, ou évaluez vos nouveaux prospects. Il vous manque un point de données spécifique ? Nous l'ajouterons pour vous en quelques jours seulement.",
      "title": "Enrichissez des entreprises par domaine, email ou URL de réseau social"
    },
    "search": {
      "badge": "Fonctionnalités de recherche",
      "description": "Interrogez notre base de données de entreprises par industrie, localisation, nombre d'employés, et 50+ autres critères. Construisez votre propre moteur de recherche pour votre produit ou sauvegardez les entreprises qui correspondent à votre profil de client idéal.",
      "title": "Recherchez des entreprises avec des conditions, par nom ou en utilisant un prompt"
    }
  }
}
</i18n>
